<template>
  <div>
    <div v-if="order" class="page-content">
      <div class="heading">{{ $t('commissioning.page_title') }}</div>

      <NewGlassCard class="card-without-title default-mb" title="">
        <div class="header_container">
          <v-btn class="back-button" @click="goOnePageBack()">
            <v-icon color="#6E6E6E">mdi-arrow-left</v-icon>
          </v-btn>

          <div class="header_text">
            <div class="header_customer">
              {{ $t('commissioning.details.customer') }}:
              {{ applicantLabel }}
            </div>
            <div class="header_address">
              {{ $t('commissioning.details.connection_object') }}:
              {{ installationAddress }}
            </div>
          </div>

          <v-btn
            rounded
            style="color: white; box-shadow: none; pointer-events: none"
            class="ml-2 mr-2"
            :color="statusConfig[1]"
          >
            {{ statusConfig[0] }}
          </v-btn>
          <div class="header_action_container">
            <v-btn
              v-show="showCancelOrderButton"
              class="mr-4 cancel_btn"
              @click="openCancellationDialog"
            >
              {{ $t('commissioning.cancel_order') }}
            </v-btn>
            <v-btn
              v-show="showStartTechnicalInspectionButton"
              class="mr-4"
              color="primary"
              @click="startInspection"
            >
              {{ $t('consumption_devices.order_details.start_inspection') }}
            </v-btn>
            <v-btn
              v-show="showCompleteWmsbReviewButton"
              class="mr-4 wmsb-review-completion-button"
              color="primary"
              :loading="wmsbReviewCompletionLoading"
              :disabled="wmsbReviewCompletionLoading"
              @click="completeWmsbReview"
            >
              {{ $t('commissioning.complete_wmsb_review') }}
            </v-btn>
          </div>
        </div>
      </NewGlassCard>

      <div
        :class="
          this.$vuetify.breakpoint.xl ? 'commissioning-details-layout' : ''
        "
      >
        <div
          :class="
            this.$vuetify.breakpoint.xl ? 'commissioning-details-main' : ''
          "
        >
          <NewGlassCard
            v-if="showInspectionChecklist"
            class="default-mb"
            title="Anmeldung prüfen"
          >
            <template v-if="isSiteElectricity">
              <v-btn
                color="primary"
                :loading="siteInspectionCompletionLoading"
                :disabled="
                  siteInspectionCompletionLoading || inspectionRejectionActive
                "
                @click="completeSiteInspection"
              >
                geprüft
              </v-btn>
              <CommissioningInspectionRejection
                :key="order.id"
                :order="order"
                :disabled="siteInspectionCompletionLoading"
                @active-change="inspectionRejectionActive = $event"
                @update-order="updateLocalOrder"
              />
              <v-alert
                v-if="siteInspectionCompletionError"
                type="error"
                text
                class="mt-4"
              >
                Prüfung konnte nicht abgeschlossen werden. Bitte erneut
                versuchen.
              </v-alert>
            </template>
            <CommissioningInspectionComponent
              v-else
              :order="order"
              @update-order="updateLocalOrder"
              @edit-industry="onEditIndustry"
            />
          </NewGlassCard>

          <NewGlassCard
            class="default-mb"
            :title="$t('commissioning.detail_title')"
          >
            <single-raw
              first
              class="single-raw"
              :title="$t('commissioning.details.request_date')"
              :editable="false"
            >
              {{ formattedRequestDate }}
            </single-raw>

            <single-raw
              class="single-raw"
              :title="$t('commissioning.details.case_number')"
              :editable="false"
            >
              {{ order.case_number || '-' }}
            </single-raw>

            <single-raw
              class="single-raw"
              :title="$t('commissioning.details.product_group')"
              :editable="false"
            >
              {{ journeyLabel }}
            </single-raw>

            <single-raw
              v-for="(detailRow, index) in detailRows"
              :key="detailRow.key"
              class="single-raw"
              :title="detailRow.title"
              :editable="detailRow.editable"
              :last="index === detailRows.length - 1"
              @edit="onEditRow(detailRow)"
            >
              <div v-html="detailRow.summary"></div>
            </single-raw>
          </NewGlassCard>

          <NewGlassCard
            v-if="!hasDetailRowsJourney"
            class="default-mb"
            :title="$t('commissioning.details.not_available_title')"
          >
            <div class="info-copy">
              {{ $t('commissioning.details.not_available_copy') }}
            </div>
          </NewGlassCard>
        </div>

        <div
          :class="
            this.$vuetify.breakpoint.xl ? 'commissioning-details-side' : ''
          "
        >
          <NewGlassCard
            :class="
              this.$vuetify.breakpoint.xl ? 'default-mb ml-2' : 'default-mb'
            "
            class="z-index-0"
            :title="$t('document_history.title')"
          >
            <DocumentHistoryComponent :historyType="'Commissioning'" />
          </NewGlassCard>
          <NewGlassCard
            :class="
              this.$vuetify.breakpoint.xl ? 'default-mb ml-2' : 'default-mb'
            "
            class="z-index-0"
            :title="$t('history.title')"
          >
            <HistoryTable :historyType="'Commissioning'" />
          </NewGlassCard>
        </div>
      </div>

      <NewModalDialog
        v-if="showCancellationDialog"
        :value="cancellationNote"
        role="dialog"
        @update-note-data="cancellationNote.note = $event"
        @cancel-data="closeCancellationDialog"
      >
        <template v-slot:body>
          <div class="dialog-title">
            {{ $t('commissioning.cancel_dialog_title') }}
          </div>
        </template>
        <template v-slot:footer>
          <div class="cancellation-dialog-footer">
            <div v-if="showCancellationReasonError" class="error-message">
              {{ $t('commissioning.cancellation_reason_required') }}
            </div>
            <div v-if="showCancellationRequestError" class="error-message">
              {{ $t('commissioning.cancellation_failed') }}
            </div>
            <div class="cancellation-dialog-actions">
              <v-btn
                color="primary"
                :loading="cancellationLoading"
                :disabled="cancellationLoading"
                @click="cancelOrder"
              >
                {{ $t('commissioning.cancel_order') }}
              </v-btn>
              <v-btn
                text
                :disabled="cancellationLoading"
                @click="closeCancellationDialog"
              >
                {{ $t('commissioning.abort') }}
              </v-btn>
            </div>
          </div>
        </template>
      </NewModalDialog>
    </div>

    <div v-else-if="isServerError">
      <h1>{{ $t('commissioning.details.loading_error') }}</h1>
      <p>{{ $t('commissioning.details.error_hint') }}</p>
    </div>
  </div>
</template>

<script>
import {
  NewGlassCard,
  NewModalDialog,
} from '@newnetzportal/netzportal-webapp-storybook-components';
import gql from 'graphql-tag';
import { CITY_TITLE, COMPANY_TITLE } from '@/helpers/constants';
import { formatCommissioningContactPerson } from '@/helpers/commissioningContactPerson';
import {
  formatCommissioningBusinessType,
  isCommissioningCustomerIndustryRequired,
} from '@/helpers/commissioningBusinessType';
import { formatCommissioningMeasureReason } from '@/helpers/commissioningMeasureReason';
import { formatDate } from '@/helpers/transformToPresentableData';
import { getCommissioningStatusDisplay } from '@/helpers/commissioningStatuses';
import {
  buildJourneyEditUrl,
  getSystemChangeEditTarget,
} from '@/helpers/commissioningJourneyLinks';
import {
  hasCommissioningSystemChangeDetails,
  splitLegacySystemChangeRemark,
  usesCommissioningInstallationDetails,
} from '@/helpers/commissioningSystemChange';
import SingleRaw from '@/components/order-details/SingleRaw';
import HistoryTable from '@/components/history-table/HistoryTable';
import DocumentHistoryComponent from '@/components/order-details/DocumentHistoryComponent';
import CommissioningInspectionComponent from '@/components/commissioning-details/CommissioningInspectionComponent.vue';
import CommissioningInspectionRejection from '@/components/commissioning-details/CommissioningInspectionRejection.vue';
import noteService from '@/services/NoteService';
import {
  getBusinessValues,
  getCommissioningOrderById,
  updateCommissioningOrderStatus as updateCommissioningOrderStatusRequest,
} from '@/services/CommissioningService';
import documentHistoryService from '@/services/DocumentHistoryService';
import { EventBus } from '@/main';

function formatAddress(address) {
  if (!address) {
    return '-';
  }

  const street = [address.street, address.street_number]
    .filter(Boolean)
    .join(' ');
  const city = [address.zip, address.city].filter(Boolean).join(' ');

  return [street, city].filter(Boolean).join(', ') || '-';
}

function getConnectionAddressLines(payload = {}) {
  const address = payload.connection_address || {};
  const locator = payload.connection_address_locator || {};
  const addressMode = address.address_mode || locator.addressMode;

  if (addressMode !== 'plot') {
    return [formatAddress(address)];
  }

  return [
    [address.zip || locator.zip, address.city || locator.city]
      .filter(Boolean)
      .join(' '),
    address.street || locator.street,
    locator.district || address.district
      ? `Gemarkung: ${locator.district || address.district}`
      : null,
    locator.landing || address.landing
      ? `Flur: ${locator.landing || address.landing}`
      : null,
    locator.plot_number || address.plot_number
      ? `Flurstück: ${locator.plot_number || address.plot_number}`
      : null,
  ].filter(Boolean);
}

function formatCoordinates(coordinates) {
  if (!coordinates) {
    return '-';
  }

  return `${coordinates.lat}, ${coordinates.lng}`;
}

function escapeHtml(value) {
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function formatOptionalValue(value) {
  return value === null || value === undefined || value === ''
    ? null
    : String(value);
}

function getPayload(order) {
  return order?.payload || {};
}

const JOURNEY_TYPES = Object.freeze({
  ELECTRIC_METER_ORDER: 'ELECTRIC_METER_ORDER',
  GAS_METER_ORDER: 'GAS_METER_ORDER',
  SITE_ELECTRIC_METER_ORDER: 'SITE_ELECTRIC_METER_ORDER',
  WATER_METER_ORDER: 'WATER_METER_ORDER',
});

const DETAIL_ROWS_SUPPORTED_JOURNEYS = [
  JOURNEY_TYPES.ELECTRIC_METER_ORDER,
  JOURNEY_TYPES.GAS_METER_ORDER,
  JOURNEY_TYPES.SITE_ELECTRIC_METER_ORDER,
  JOURNEY_TYPES.WATER_METER_ORDER,
];

const STANDARD_METER_ORDER_JOURNEYS = [
  JOURNEY_TYPES.ELECTRIC_METER_ORDER,
  JOURNEY_TYPES.GAS_METER_ORDER,
  JOURNEY_TYPES.WATER_METER_ORDER,
];

const EXISTING_METER_ORDER_TYPES = ['zaehlerausbau', 'zaehlerwechsel'];

const CANCELLABLE_STATUSES = [
  'ORDER_CREATED',
  'CRITICAL_ORDER_CREATED',
  'WMSB_ORDER_CREATED',
  'IN_REVIEW',
  'ORDER_TECHNICAL_FEASIBILITY',
  'ORDER_ACCEPTED',
];

function resolveOrderJourney(order) {
  const rawJourneyType =
    typeof order?.journey_type === 'string'
      ? order.journey_type.trim().toUpperCase()
      : typeof order?.payload?.journey_type === 'string'
      ? order.payload.journey_type.trim().toUpperCase()
      : '';

  if (
    rawJourneyType &&
    DETAIL_ROWS_SUPPORTED_JOURNEYS.includes(rawJourneyType)
  ) {
    return rawJourneyType;
  }

  return undefined;
}

export default {
  name: 'CommissioningDetailsPage',
  components: {
    NewGlassCard,
    NewModalDialog,
    SingleRaw,
    HistoryTable,
    DocumentHistoryComponent,
    CommissioningInspectionComponent,
    CommissioningInspectionRejection,
  },
  data() {
    return {
      order: null,
      isMeterInRegionetzArea: null,
      isServerError: false,
      showCancellationDialog: false,
      cancellationNote: { note: '' },
      cancellationLoading: false,
      showCancellationReasonError: false,
      showCancellationRequestError: false,
      wmsbReviewCompletionLoading: false,
      siteInspectionCompletionLoading: false,
      siteInspectionCompletionError: false,
      inspectionRejectionActive: false,
    };
  },
  created() {
    this.loadOrder();
  },
  computed: {
    applicantLabel() {
      const applicant = getPayload(this.order).contact_person;

      if (!applicant) {
        return '-';
      }

      return formatCommissioningContactPerson(applicant);
    },
    installationAddress() {
      return getConnectionAddressLines(getPayload(this.order)).join(' · ');
    },
    statusConfig() {
      const status = getCommissioningStatusDisplay(this.order?.status);
      return [status.label, status.color];
    },
    isSiteElectricity() {
      return this.orderJourneyType === JOURNEY_TYPES.SITE_ELECTRIC_METER_ORDER;
    },
    supportsTechnicalInspection() {
      const orderType = getPayload(this.order).order_type;
      if (this.isSiteElectricity) {
        return ['zaehlereinbau', 'zaehlerausbau'].includes(orderType);
      }

      return (
        DETAIL_ROWS_SUPPORTED_JOURNEYS.includes(this.orderJourneyType) &&
        (orderType === 'zaehlereinbau' ||
          (EXISTING_METER_ORDER_TYPES.includes(orderType) &&
            STANDARD_METER_ORDER_JOURNEYS.includes(this.orderJourneyType)))
      );
    },
    showStartTechnicalInspectionButton() {
      return (
        this.supportsTechnicalInspection &&
        ['ORDER_CREATED', 'CRITICAL_ORDER_CREATED'].includes(this.order?.status)
      );
    },
    showCompleteWmsbReviewButton() {
      return (
        String(this.order?.status || '').toUpperCase() === 'WMSB_IN_REVIEW'
      );
    },
    showCancelOrderButton() {
      return CANCELLABLE_STATUSES.includes(this.order?.status);
    },
    showInspectionChecklist() {
      return (
        this.supportsTechnicalInspection &&
        this.order?.status === 'ORDER_TECHNICAL_FEASIBILITY'
      );
    },
    canEditApplicationDetails() {
      return [
        'ORDER_CREATED',
        'CRITICAL_ORDER_CREATED',
        'ORDER_TECHNICAL_FEASIBILITY',
        'WMSB_ORDER_CREATED',
        'WMSB_IN_REVIEW',
      ].includes(this.order?.status);
    },
    userPersonalData() {
      return this.$store.getters.getUserId;
    },
    formattedRequestDate() {
      const requestDate =
        getPayload(this.order).request_date || this.order?.created_at;

      return requestDate ? formatDate(requestDate, 'DD.MM.YYYY HH:mm') : '-';
    },
    orderJourneyType() {
      return resolveOrderJourney(this.order);
    },
    hasDetailRowsJourney() {
      return DETAIL_ROWS_SUPPORTED_JOURNEYS.includes(this.orderJourneyType);
    },
    journeyLabel() {
      if (this.orderJourneyType === JOURNEY_TYPES.WATER_METER_ORDER) {
        return this.$t('clerk_orders_overview_table.water');
      }

      if (this.orderJourneyType === JOURNEY_TYPES.GAS_METER_ORDER) {
        return this.$t('clerk_orders_overview_table.gas');
      }

      if (this.orderJourneyType === JOURNEY_TYPES.ELECTRIC_METER_ORDER) {
        return this.$t('clerk_orders_overview_table.electricity');
      }

      if (this.orderJourneyType === JOURNEY_TYPES.SITE_ELECTRIC_METER_ORDER) {
        return this.$t('commissioning.details.journey_types.baustrom');
      }

      return '-';
    },
    detailRows() {
      if (!this.order) {
        return [];
      }

      if (!this.hasDetailRowsJourney) {
        return [];
      }

      const payload = getPayload(this.order);
      const isSiteElectricOrder =
        this.orderJourneyType === JOURNEY_TYPES.SITE_ELECTRIC_METER_ORDER;
      const hasSystemChangeDetails =
        hasCommissioningSystemChangeDetails(payload);
      // Only WAÄ Wasser collects the usage, WAÄ Gas/Strom only the appliances.
      const hasSystemChangeUsage =
        hasSystemChangeDetails &&
        Array.isArray(payload.usage_types) &&
        payload.usage_types.length > 0;
      const hasInstallationDetails =
        usesCommissioningInstallationDetails(payload);
      const rows = [
        {
          key: 'connection-address',
          title: this.$t(
            'commissioning.details.substep_labels.connection_address'
          ),
          summary: this.buildConnectionAddressSummary(payload),
          editable: this.canEditApplicationDetails,
        },
        {
          key: 'meter-position',
          title: this.$t('commissioning.details.substep_labels.meter_position'),
          summary: this.buildMeterPositionSummary(payload),
          editable: this.canEditApplicationDetails,
        },
      ];

      if (!isSiteElectricOrder) {
        rows.push({
          key: 'unit',
          title: this.$t('commissioning.details.substep_labels.unit'),
          summary: this.buildUnitSummary(payload),
          editable: this.canEditApplicationDetails,
        });
      }

      rows.push({
        key: 'measure-type',
        title: this.$t('commissioning.details.substep_labels.measure_type'),
        summary: this.buildOrderTypeSummary(payload),
        editable: false,
      });

      if (payload.order_type !== 'zaehlereinbau' && !isSiteElectricOrder) {
        rows.push({
          key: 'measure-reason',
          title: this.$t('commissioning.details.fields.measure_reason'),
          summary: this.buildMeasureReasonSummary(payload),
          editable: this.canEditApplicationDetails,
        });
      }

      if (
        (payload.order_type === 'zaehlereinbau' || hasSystemChangeUsage) &&
        !isSiteElectricOrder
      ) {
        rows.push({
          key: 'business-type',
          title: this.$t('commissioning.details.fields.object_business_type'),
          summary: this.buildBusinessTypeSummary(payload),
          editable: this.canEditApplicationDetails,
        });
      }

      if (this.orderJourneyType === JOURNEY_TYPES.ELECTRIC_METER_ORDER) {
        rows.push({
          key: 'meter-type',
          title: this.$t('commissioning.details.substep_labels.meter_type'),
          summary: this.buildMeterTypeSummary(payload),
          editable: this.canEditApplicationDetails,
        });
      }

      if (payload.order_type === 'zaehlereinbau') {
        rows.push({
          key: 'additional-meter-installation',
          title: this.$t(
            'commissioning.details.substep_labels.additional_meter_installation'
          ),
          summary: isSiteElectricOrder
            ? this.buildSiteElectricInstallationSummary(payload)
            : this.buildAdditionalMeterInstallationSummary(payload),
          editable: this.canEditApplicationDetails,
        });
      }

      if (
        this.orderJourneyType === JOURNEY_TYPES.ELECTRIC_METER_ORDER &&
        payload.order_type === 'zaehlerwechsel'
      ) {
        rows.push({
          key: 'meter-bidirectional',
          title: this.$t(
            'commissioning.details.substep_labels.meter_bidirectional'
          ),
          summary: this.buildMeterBidirectionalSummary(payload),
          editable: this.canEditApplicationDetails,
        });
      }

      const hasCounterData =
        (payload.order_type === 'zaehlerausbau' ||
          payload.order_type === 'zaehlerwechsel') &&
        Boolean(payload.counter_id || payload.picture?.picture_name);

      if (hasCounterData) {
        rows.push({
          key: 'counter-data',
          title: this.$t('commissioning.details.substep_labels.counter_data'),
          summary: this.buildCounterDataSummary(payload),
          editable: this.canEditApplicationDetails,
        });
      }

      if (!isSiteElectricOrder && (!hasCounterData || hasSystemChangeUsage)) {
        rows.push({
          key: 'usage-type',
          title: this.$t('commissioning.details.substep_labels.usage_type'),
          summary: this.buildUsageTypeSummary(payload),
          editable: this.canEditApplicationDetails,
        });
      }

      if (isSiteElectricOrder && payload.order_type === 'zaehlereinbau') {
        rows.push({
          key: 'technical-electric-data',
          title: this.$t(
            'commissioning.details.substep_labels.site_electric_power_data'
          ),
          summary: this.buildTechnicalElectricDataSummary(payload),
          editable: this.canEditApplicationDetails,
        });
      }

      if (
        isSiteElectricOrder &&
        typeof payload.has_existing_connection === 'boolean'
      ) {
        rows.push({
          key: 'existing-connection',
          title: this.$t('commissioning.details.fields.existing_connection'),
          summary: this.buildExistingConnectionSummary(payload),
          editable: false,
        });
      }

      if (
        this.orderJourneyType === JOURNEY_TYPES.GAS_METER_ORDER &&
        hasInstallationDetails
      ) {
        rows.push(
          {
            key: 'gas-appliances',
            title: this.$t(
              'commissioning.details.substep_labels.gas_appliances'
            ),
            summary: this.buildGasAppliancesSummary(payload),
            editable: this.canEditApplicationDetails,
          },
          {
            key: 'meter-size',
            title: this.$t(
              'commissioning.details.substep_labels.technical_gas_data'
            ),
            summary: this.buildGasMeterSizeSummary(payload),
            editable: this.canEditApplicationDetails,
          }
        );
      }

      if (
        this.orderJourneyType === JOURNEY_TYPES.ELECTRIC_METER_ORDER &&
        hasInstallationDetails
      ) {
        rows.push(
          {
            key: 'power-appliances',
            title: this.$t(
              'commissioning.details.substep_labels.power_appliances'
            ),
            summary: this.buildElectricAppliancesSummary(payload),
            editable: this.canEditApplicationDetails,
          },
          {
            key: 'technical-electric-data',
            title: this.$t(
              'commissioning.details.substep_labels.technical_electric_data'
            ),
            summary: this.buildTechnicalElectricDataSummary(payload),
            editable: this.canEditApplicationDetails,
          }
        );

        if (payload.feedin) {
          rows.push({
            key: 'feedin',
            title: this.$t('commissioning.details.substep_labels.feedin'),
            summary: this.buildFeedinSummary(payload),
            editable: this.canEditApplicationDetails,
          });
        }
      }

      rows.push(
        {
          key: 'installation-company',
          title: this.$t(
            'commissioning.details.substep_labels.installation_company'
          ),
          summary: this.buildInstallationCompanySummary(payload),
          editable: false,
        },
        {
          key: 'responsible-professional',
          title: this.$t(
            'commissioning.details.substep_labels.responsible_professional'
          ),
          summary: this.buildResponsibleProfessionalSummary(payload),
          editable: false,
        },
        {
          key: 'personal-data',
          title: this.$t('commissioning.details.substep_labels.personal_data'),
          summary: this.buildPersonalDataSummary(payload),
          editable: this.canEditApplicationDetails,
        },
        ...(this.requiresCustomerIndustry(payload.contact_person) ||
        payload.contact_person?.business_type
          ? [
              {
                key: 'customer-business-type',
                title: this.$t(
                  'commissioning.details.fields.customer_business_type'
                ),
                summary: this.buildCustomerBusinessTypeSummary(payload),
                editable: this.canEditApplicationDetails,
              },
            ]
          : []),
        {
          key: 'customer-address',
          title: this.$t(
            'commissioning.details.substep_labels.customer_address'
          ),
          summary: this.buildCustomerAddressSummary(payload),
          editable: this.canEditApplicationDetails,
        },
        ...(payload.alternate_invoice_recipient
          ? [
              {
                key: 'alternate-invoice-recipient',
                title: this.$t(
                  'commissioning.details.substep_labels.alternate_invoice_recipient'
                ),
                summary: this.buildAlternateInvoiceRecipientSummary(payload),
                editable: this.canEditApplicationDetails,
              },
            ]
          : []),
        {
          key: 'contact-details',
          title: this.$t(
            isSiteElectricOrder
              ? 'commissioning.details.substep_labels.invoice_recipient_contact_details'
              : 'commissioning.details.substep_labels.contact_details'
          ),
          summary: this.buildContactDetailsSummary(payload),
          editable: this.canEditApplicationDetails,
        },
        {
          key: 'application-remarks',
          title: this.$t(
            'commissioning.details.substep_labels.application_remarks'
          ),
          summary: this.buildApplicationRemarksSummary(payload),
          editable: this.canEditApplicationDetails,
        }
      );

      const technicalInspectionData =
        payload.technical_inspection_data ||
        this.order?.technical_inspection_data;

      if (technicalInspectionData) {
        rows.push({
          key: 'technical-inspection',
          title: 'Technische Prüfung',
          summary: this.buildTechnicalInspectionSummary(
            technicalInspectionData
          ),
          editable: false,
        });
      }

      return rows;
    },
  },
  methods: {
    getChangedValue(valueName, responseObject) {
      let obj = null;
      if (responseObject[valueName]) {
        obj = JSON.parse(responseObject[valueName]);
      } else if (responseObject.workflow_payload) {
        const tmp = JSON.parse(responseObject.workflow_payload);
        valueName === 'new_value'
          ? (obj = { status: tmp.new_status })
          : (obj = { status: tmp.old_status });
      }
      return obj;
    },
    parseHistoryStatus(status) {
      if (status === null || status === undefined || status === '') {
        return null;
      }

      if (typeof status !== 'string') {
        return status;
      }

      try {
        return JSON.parse(status);
      } catch (error) {
        return status;
      }
    },
    async emitLiveCommissioningDocumentHistoryEvent(eventData) {
      const wait = (ms) =>
        new Promise((resolve) => {
          setTimeout(resolve, ms);
        });

      for (let attempt = 0; attempt < 10; attempt += 1) {
        try {
          const response =
            await documentHistoryService.getCommissioningDocumentsHistory({
              id: this.$route.params.id,
              from_index: 0,
              limit: 50,
            });
          const changes =
            response?.commissioningDocumentsHistory?.changes || [];
          const matchingChange = changes.find((change) => {
            try {
              const payload = JSON.parse(change.payload);
              return (
                payload.filename === eventData.filename ||
                payload.filename?.endsWith(`/${eventData.filename}`)
              );
            } catch (error) {
              console.error(
                'Error parsing commissioning document history payload:',
                error
              );
              return false;
            }
          });

          if (matchingChange) {
            EventBus.$emit(
              'update-history-table-commissioning',
              matchingChange
            );
            return;
          }
        } catch (error) {
          console.error(
            'Error fetching commissioning document history after live update:',
            error
          );
        }

        await wait(500);
      }
    },
    async loadOrder() {
      const order = await getCommissioningOrderById(this.$route.params.id);

      if (!order) {
        this.isServerError = true;
        return;
      }

      const hydratedOrder = {
        ...order,
        product: order.product,
        products: order.products,
        user_id: getPayload(order).user_id,
      };

      if (hydratedOrder.status === 'WMSB_ORDER_CREATED') {
        const res = await updateCommissioningOrderStatusRequest(
          {
            id: hydratedOrder.id,
            old_value: { status: 'WMSB_ORDER_CREATED' },
            new_value: { status: 'WMSB_IN_REVIEW' },
          },
          this.userPersonalData
        );

        if (res) {
          hydratedOrder.status = 'WMSB_IN_REVIEW';
        } else {
          console.error(
            'Failed to auto-transition WMSB status from created to review.'
          );
        }
      }

      this.order = hydratedOrder;
      this.$store.commit('setSelectedCommissioning', this.order);
      this.isServerError = false;
      this.loadRegionetzArea();
    },
    goOnePageBack() {
      const previousPage = this.$store.getters.getPreviousPage;
      if (previousPage) {
        this.$router.push(previousPage);
      } else {
        this.$router.push(
          '/inbetriebsetzung?page=1&journey=all&order_status=all'
        );
      }
    },
    startInspection() {
      this.updateCommissioningOrderStatus('ORDER_TECHNICAL_FEASIBILITY');
    },
    async completeSiteInspection() {
      if (
        !this.isSiteElectricity ||
        !this.showInspectionChecklist ||
        this.inspectionRejectionActive ||
        this.siteInspectionCompletionLoading
      ) {
        return;
      }
      this.siteInspectionCompletionLoading = true;
      this.siteInspectionCompletionError = false;
      try {
        const response = await updateCommissioningOrderStatusRequest(
          {
            id: this.order.id,
            old_value: { status: this.order.status },
            new_value: { status: 'TRANSMITTED' },
          },
          this.userPersonalData
        );
        if (!response || response.error) {
          this.siteInspectionCompletionError = true;
          return;
        }
        this.updateLocalOrder({ status: 'TRANSMITTED' });
      } catch {
        this.siteInspectionCompletionError = true;
      } finally {
        this.siteInspectionCompletionLoading = false;
      }
    },
    async completeWmsbReview() {
      this.wmsbReviewCompletionLoading = true;

      try {
        const response = await updateCommissioningOrderStatusRequest(
          {
            id: this.order.id,
            old_value: { status: this.order.status },
            new_value: { status: 'WMSB_TRANSMITTED' },
          },
          this.userPersonalData
        );

        if (response) {
          this.updateLocalOrder({ status: 'WMSB_TRANSMITTED' });
        }
      } catch (error) {
        console.log(error);
      } finally {
        this.wmsbReviewCompletionLoading = false;
      }
    },
    openCancellationDialog() {
      this.cancellationNote = { note: '' };
      this.showCancellationReasonError = false;
      this.showCancellationRequestError = false;
      this.showCancellationDialog = true;
    },
    closeCancellationDialog() {
      if (this.cancellationLoading) {
        return;
      }

      this.showCancellationDialog = false;
      this.cancellationNote = { note: '' };
      this.showCancellationReasonError = false;
      this.showCancellationRequestError = false;
    },
    async cancelOrder() {
      const reason = this.cancellationNote.note.trim();

      if (!reason) {
        this.showCancellationReasonError = true;
        return;
      }

      this.cancellationLoading = true;
      this.showCancellationReasonError = false;
      this.showCancellationRequestError = false;

      const noteResponse = await noteService.addCommissioningNote(
        {
          id: this.order.id,
          user_id: this.order.user_id,
          title: 'Stornierungsgrund',
          note: reason,
        },
        this.userPersonalData
      );

      if (!noteResponse) {
        this.cancellationLoading = false;
        this.showCancellationRequestError = true;
        return;
      }

      const statusResponse = await updateCommissioningOrderStatusRequest(
        {
          id: this.order.id,
          old_value: { status: this.order.status },
          new_value: { status: 'ORDER_CANCELLED' },
          remark: reason,
        },
        this.userPersonalData
      );

      this.cancellationLoading = false;

      if (!statusResponse) {
        this.showCancellationRequestError = true;
        return;
      }

      this.updateLocalOrder({ status: 'ORDER_CANCELLED' });
      this.closeCancellationDialog();
    },
    updateCommissioningOrderStatus(status) {
      updateCommissioningOrderStatusRequest(
        {
          id: this.order.id,
          old_value: {
            status: this.order.status,
          },
          new_value: {
            status,
          },
        },
        this.userPersonalData
      )
        .then((res) => {
          if (res) {
            const newStatus = { status };
            this.$store.commit('updateSelectedCommissioningData', newStatus);
            this.order = {
              ...this.order,
              ...newStatus,
            };
          }
        })
        .catch((error) => {
          console.log(error);
        });
    },
    updateLocalOrder(newValue) {
      if (
        typeof newValue?.technical_inspection_data
          ?.is_meter_in_regionetz_area === 'boolean'
      ) {
        this.isMeterInRegionetzArea =
          newValue.technical_inspection_data.is_meter_in_regionetz_area;
      }
      const {
        technical_inspection_data,
        payload = {},
        ...orderData
      } = newValue;
      this.$store.commit('updateSelectedCommissioningData', orderData);
      if (technical_inspection_data) {
        this.$store.commit('updateSelectedCommissioningPayloadData', {
          ...payload,
          technical_inspection_data,
        });
      }
      this.order = {
        ...this.order,
        ...orderData,
        payload: {
          ...this.order.payload,
          ...payload,
          ...(technical_inspection_data ? { technical_inspection_data } : {}),
        },
      };
    },
    onEditRow(detailRow) {
      if (!detailRow.editable) {
        return;
      }

      let url = null;
      const targetOverride = hasCommissioningSystemChangeDetails(
        getPayload(this.order)
      )
        ? getSystemChangeEditTarget(this.orderJourneyType, detailRow.key)
        : null;

      switch (this.orderJourneyType) {
        case JOURNEY_TYPES.WATER_METER_ORDER:
          url = buildJourneyEditUrl(
            JOURNEY_TYPES.WATER_METER_ORDER,
            detailRow.key,
            this.order?.id || this.$route.params.id,
            targetOverride
          );
          break;
        case JOURNEY_TYPES.ELECTRIC_METER_ORDER:
          url = buildJourneyEditUrl(
            JOURNEY_TYPES.ELECTRIC_METER_ORDER,
            detailRow.key,
            this.order?.id || this.$route.params.id,
            targetOverride
          );
          break;
        case JOURNEY_TYPES.GAS_METER_ORDER:
          url = buildJourneyEditUrl(
            JOURNEY_TYPES.GAS_METER_ORDER,
            detailRow.key,
            this.order?.id || this.$route.params.id,
            targetOverride
          );
          break;
        case JOURNEY_TYPES.SITE_ELECTRIC_METER_ORDER:
          url = buildJourneyEditUrl(
            JOURNEY_TYPES.SITE_ELECTRIC_METER_ORDER,
            detailRow.key,
            this.order?.id || this.$route.params.id
          );
          break;
        default:
          break;
      }

      if (url) {
        window.open(url, '_blank', 'noopener');
      } else {
        console.warn(
          `Editing for detail row "${detailRow.key}" is not implemented for journey_type "${this.orderJourneyType}".`
        );
      }
    },
    onEditIndustry(rowKey) {
      const detailRow = this.detailRows.find((row) => row.key === rowKey);

      if (detailRow) {
        this.onEditRow(detailRow);
      }
    },
    requiresCustomerIndustry(contactPerson = {}) {
      return isCommissioningCustomerIndustryRequired(contactPerson);
    },
    buildConnectionAddressSummary(payload = {}) {
      const address = this.joinContentLines(getConnectionAddressLines(payload));
      const showRegionetzArea =
        this.orderJourneyType === JOURNEY_TYPES.GAS_METER_ORDER &&
        (payload.order_type === 'zaehlereinbau' ||
          EXISTING_METER_ORDER_TYPES.includes(payload.order_type)) &&
        typeof this.isMeterInRegionetzArea === 'boolean';

      if (!showRegionetzArea) {
        return address;
      }

      const regionetzArea = this.isMeterInRegionetzArea ? 'Ja' : 'Nein';
      return `${address}<br><br>Zähler im Bereich Regionetz: <strong>${regionetzArea}</strong>`;
    },
    async loadRegionetzArea() {
      const payload = getPayload(this.order);
      if (
        this.orderJourneyType !== JOURNEY_TYPES.GAS_METER_ORDER ||
        (payload.order_type !== 'zaehlereinbau' &&
          !EXISTING_METER_ORDER_TYPES.includes(payload.order_type))
      ) {
        return;
      }

      const savedInspectionData =
        payload.technical_inspection_data ||
        this.order?.technical_inspection_data;
      if (
        typeof savedInspectionData?.is_meter_in_regionetz_area === 'boolean'
      ) {
        this.isMeterInRegionetzArea =
          savedInspectionData.is_meter_in_regionetz_area;
      }

      const zip = String(
        payload.connection_address?.zip ||
          payload.connection_address_locator?.zip ||
          ''
      ).trim();
      if (!zip) {
        return;
      }

      const values = await getBusinessValues(
        'NetzbetreiberPLZ',
        `${zip},Gas,Regionetz`
      );
      if (Array.isArray(values)) {
        this.isMeterInRegionetzArea = values.length > 0;
      }
    },
    buildMeterPositionSummary(payload = {}) {
      const meterPosition = payload.meter_position || {};
      return this.joinContentLines([
        meterPosition.description,
        `${this.$t(
          'commissioning.details.fields.meter_position_coordinates'
        )}: ${formatCoordinates(meterPosition.coordinates)}`,
      ]);
    },
    buildUnitSummary(payload = {}) {
      return this.joinContentLines([payload.unit]);
    },
    buildMeterTypeSummary(payload = {}) {
      return this.joinContentLines([
        payload.meter_type,
        typeof payload.feedin === 'boolean'
          ? `${this.$t('commissioning.details.fields.feedin')}: ${this.$t(
              `commissioning.details.boolean_values.${payload.feedin}`
            )}`
          : null,
      ]);
    },
    buildOrderTypeSummary(payload = {}) {
      return this.joinContentLines([
        payload.order_type
          ? this.$t(`commissioning.details.measure_types.${payload.order_type}`)
          : null,
      ]);
    },
    buildMeasureReasonSummary(payload = {}) {
      const { connectionPower } = splitLegacySystemChangeRemark(payload);
      return this.joinContentLines([
        formatCommissioningMeasureReason(payload.measure_reason),
        connectionPower ? `Anschlussleistung: ${connectionPower}` : null,
      ]);
    },
    buildBusinessTypeSummary(payload = {}) {
      return this.joinContentLines([
        formatCommissioningBusinessType(payload.business_type),
      ]);
    },
    buildCustomerBusinessTypeSummary(payload = {}) {
      return this.joinContentLines([
        formatCommissioningBusinessType(payload.contact_person?.business_type),
      ]);
    },
    buildAdditionalMeterInstallationSummary(payload = {}) {
      return this.joinContentLines([
        payload.building_type
          ? `${this.$t(
              'commissioning.details.fields.building_type'
            )}: ${this.$t(
              `commissioning.details.building_types.${payload.building_type}`
            )}`
          : null,
        typeof payload.wmsb === 'boolean'
          ? `${this.$t('commissioning.details.fields.wmsb')}: ${this.$t(
              `commissioning.details.boolean_values.${payload.wmsb}`
            )}${
              payload.wmsb && payload.wmsb_name ? ` (${payload.wmsb_name})` : ''
            }`
          : null,
        typeof payload.recommissioning === 'boolean'
          ? `${this.$t(
              'commissioning.details.fields.recommissioning'
            )}: ${this.$t(
              `commissioning.details.boolean_values.${payload.recommissioning}`
            )}`
          : null,
      ]);
    },
    buildSiteElectricInstallationSummary(payload = {}) {
      return this.joinContentLines([
        typeof payload.early_house_connection === 'boolean'
          ? `${this.$t(
              'commissioning.details.fields.early_house_connection'
            )}: ${this.$t(
              `commissioning.details.boolean_values.${payload.early_house_connection}`
            )}`
          : null,
      ]);
    },
    buildMeterBidirectionalSummary(payload = {}) {
      return this.joinContentLines([
        typeof payload.meter_bidirectional === 'boolean'
          ? `${this.$t(
              'commissioning.details.fields.meter_bidirectional'
            )}: ${this.$t(
              `commissioning.details.boolean_values.${payload.meter_bidirectional}`
            )}`
          : null,
      ]);
    },
    buildUsageTypeSummary(payload = {}) {
      const usageTypes = Array.isArray(payload.usage_types)
        ? payload.usage_types
            .map((usageType) =>
              usageType
                ? this.$t(`commissioning.details.usage_types.${usageType}`)
                : null
            )
            .filter(Boolean)
            .join(', ')
        : payload.usage_type
        ? this.$t(`commissioning.details.usage_types.${payload.usage_type}`)
        : null;
      const meterSize =
        this.orderJourneyType === JOURNEY_TYPES.WATER_METER_ORDER
          ? payload.meter_size
          : null;

      return this.joinContentLines([
        usageTypes,
        payload.residential_units
          ? `${this.$t('commissioning.details.fields.residential_units')}: ${
              payload.residential_units
            }`
          : null,
        payload.flow_rate_m3_h
          ? `${this.$t('commissioning.details.fields.flow_rate')}: ${String(
              payload.flow_rate_m3_h
            ).replace('.', ',')} m³/h`
          : null,
        meterSize
          ? `${this.$t(
              'commissioning.details.fields.meter_size'
            )}: ${meterSize}`
          : null,
      ]);
    },
    buildGasMeterSizeSummary(payload = {}) {
      return this.joinContentLines([
        payload.expected_annual_consumption_kwh
          ? `${this.$t(
              'commissioning.details.fields.expected_annual_consumption'
            )}: ${payload.expected_annual_consumption_kwh} kWh`
          : null,
        payload.total_nominal_load_kw
          ? `${this.$t('commissioning.details.fields.total_nominal_load')}: ${
              payload.total_nominal_load_kw
            } kW`
          : null,
        payload.suggested_meter_size
          ? `${this.$t('commissioning.details.fields.meter_size')}: ${
              payload.suggested_meter_size
            }`
          : null,
      ]);
    },
    buildGasAppliancesSummary(payload = {}) {
      const appliances = Array.isArray(payload.power_appliances)
        ? payload.power_appliances
        : [];

      return this.joinContentLines(
        appliances.map((appliance) => {
          const typeLabel =
            appliance.type === 'sonstiges' && appliance.label
              ? appliance.label
              : this.$t(
                  `commissioning.details.gas_appliance_types.${appliance.type}`
                );
          const load = appliance.nominal_load_kw ?? appliance.power_kw;
          const parts = [load != null ? `${typeLabel}: ${load} kW` : typeLabel];

          if (appliance.heating_load_kw != null) {
            parts.push(
              `${this.$t('commissioning.details.fields.heating_load')}: ${
                appliance.heating_load_kw
              } kW`
            );
          }

          return parts.join(', ');
        })
      );
    },
    buildElectricAppliancesSummary(payload = {}) {
      const appliances = Array.isArray(payload.power_appliances)
        ? payload.power_appliances
        : [];

      return this.joinContentLines(
        appliances.map((appliance) => {
          const typeLabel = this.$t(
            `commissioning.details.electric_appliance_types.${appliance.type}`
          );
          const load = appliance.power_kw ?? appliance.nominal_load_kw;

          return load != null ? `${typeLabel}: ${load} kW` : typeLabel;
        })
      );
    },
    buildTechnicalElectricDataSummary(payload = {}) {
      return this.joinContentLines([
        payload.simultaneously_required_kw
          ? `${this.$t(
              'commissioning.details.fields.simultaneously_required'
            )}: ${payload.simultaneously_required_kw} kW`
          : null,
        payload.expected_annual_consumption_kwh
          ? `${this.$t(
              'commissioning.details.fields.expected_annual_consumption'
            )}: ${payload.expected_annual_consumption_kwh} kWh`
          : null,
      ]);
    },
    buildFeedinSummary(payload = {}) {
      return this.joinContentLines([
        payload.feedin_size
          ? `${this.$t('commissioning.details.fields.feedin_size')}: ${
              payload.feedin_size
            } kW`
          : null,
        payload.expected_feedin_kwh
          ? `${this.$t('commissioning.details.fields.expected_feedin')}: ${
              payload.expected_feedin_kwh
            } kWh`
          : null,
      ]);
    },
    buildCounterDataSummary(payload = {}) {
      return this.joinContentLines([
        payload.counter_id
          ? `${this.$t('commissioning.details.fields.counter_number')}: ${
              payload.counter_id
            }`
          : null,
        payload.picture?.picture_name
          ? `${this.$t('commissioning.details.fields.counter_picture')}: ${
              payload.picture.picture_name
            }`
          : null,
      ]);
    },
    buildInstallationCompanySummary(payload = {}) {
      return this.joinContentLines([payload.installation_company]);
    },
    buildPersonalDataSummary(payload = {}) {
      const contactPerson = payload.contact_person || {};

      if ([COMPANY_TITLE, CITY_TITLE].includes(contactPerson.title)) {
        const organizationName =
          contactPerson.organization_name ||
          contactPerson.company_name ||
          contactPerson.city_name;

        return this.joinContentLines([
          [contactPerson.title, organizationName].filter(Boolean).join(' '),
          [contactPerson.name, contactPerson.surname].filter(Boolean).join(' '),
        ]);
      }

      return this.joinContentLines([
        formatCommissioningContactPerson(contactPerson),
      ]);
    },
    buildExistingConnectionSummary(payload = {}) {
      return this.joinContentLines([
        this.$t(
          `commissioning.details.boolean_values.${payload.has_existing_connection}`
        ),
      ]);
    },
    buildCustomerAddressSummary(payload = {}) {
      const contactPerson = payload.contact_person || {};
      return this.joinContentLines([formatAddress(contactPerson.address)]);
    },
    buildAlternateInvoiceRecipientSummary(payload = {}) {
      const recipient = payload.alternate_invoice_recipient || {};
      return this.joinContentLines([
        formatCommissioningContactPerson(recipient),
        formatAddress(recipient.address),
      ]);
    },
    buildContactDetailsSummary(payload = {}) {
      const contactPerson = payload.contact_person || {};
      return this.joinContentLines([
        contactPerson.phone_number
          ? `${this.$t('commissioning.details.fields.phone_number')}: ${
              contactPerson.phone_number
            }`
          : null,
        contactPerson.email === null
          ? `${this.$t('commissioning.details.fields.email')}: ${this.$t(
              'commissioning.details.email_unknown'
            )}`
          : contactPerson.email
          ? `${this.$t('commissioning.details.fields.email')}: ${
              contactPerson.email
            }`
          : null,
      ]);
    },
    buildResponsibleProfessionalSummary(payload = {}) {
      return this.joinContentLines([payload.responsible_specialist_name]);
    },
    buildApplicationRemarksSummary(payload = {}) {
      return this.joinContentLines([
        splitLegacySystemChangeRemark(payload).remark,
      ]);
    },
    buildTechnicalInspectionSummary(inspectionData = {}) {
      const booleanLabel = (value) =>
        this.$t(`commissioning.details.boolean_values.${value}`);

      return this.joinContentLines([
        inspectionData.addition
          ? `Ergänzung: ${inspectionData.addition}`
          : null,
        typeof inspectionData.flow_rate_or_units_valid === 'boolean'
          ? `Durchflussmenge / Wohneinheiten valide: ${booleanLabel(
              inspectionData.flow_rate_or_units_valid
            )}`
          : null,
        typeof inspectionData.meter_size_matching === 'boolean'
          ? `Zählergröße passend: ${booleanLabel(
              inspectionData.meter_size_matching
            )}`
          : null,
        typeof inspectionData.counter_number_matches_photo === 'boolean'
          ? `Zählernummer passend zu Foto: ${booleanLabel(
              inspectionData.counter_number_matches_photo
            )}`
          : null,
        inspectionData.measure_reason
          ? `Sachverhalt (Ausbau-/Wechselgrund): ${inspectionData.measure_reason}`
          : null,
        inspectionData.industry_code || inspectionData.industry_text
          ? `Branche: ${[
              inspectionData.industry_text,
              inspectionData.industry_code
                ? `(${inspectionData.industry_code})`
                : null,
            ]
              .filter(Boolean)
              .join(' ')}`
          : null,
        inspectionData.installation_reason
          ? `Einbaugrund: ${inspectionData.installation_reason}`
          : null,
        inspectionData.customer_type
          ? `Kundenart: ${inspectionData.customer_type}`
          : null,
        inspectionData.partner_group_type
          ? `Partnergruppenart: ${inspectionData.partner_group_type}`
          : null,
        inspectionData.business_partner_type
          ? `Geschäftspartnerart: ${inspectionData.business_partner_type}`
          : null,
        inspectionData.special_meter !== undefined &&
        inspectionData.special_meter !== null
          ? `Sonderzähler: ${inspectionData.special_meter || 'Leer'}`
          : null,
        inspectionData.pressure_level
          ? `Druckstufe: ${inspectionData.pressure_level}`
          : null,
        inspectionData.voltage_level
          ? `Spannungsebene: ${inspectionData.voltage_level}`
          : null,
        inspectionData.counting_method
          ? `Zählverfahren: ${inspectionData.counting_method}`
          : null,
        inspectionData.electricity_grid_connection_capacity !== undefined &&
        inspectionData.electricity_grid_connection_capacity !== null
          ? `Stromnetzanschlusskapazität (kW): ${inspectionData.electricity_grid_connection_capacity}`
          : null,
        inspectionData.gas_grid_connection_capacity !== undefined &&
        inspectionData.gas_grid_connection_capacity !== null
          ? `Gasnetzanschlusskapazität (kW): ${inspectionData.gas_grid_connection_capacity}`
          : null,
        inspectionData.jvp !== undefined && inspectionData.jvp !== null
          ? `JVP (kWh/Jahr): ${inspectionData.jvp}`
          : null,
        typeof inspectionData.is_meter_in_regionetz_area === 'boolean'
          ? `Zähler im Bereich Regionetz: ${
              inspectionData.is_meter_in_regionetz_area ? 'Ja' : 'Nein'
            }`
          : null,
        typeof inspectionData.is_last_gas_meter === 'boolean'
          ? `Letzter Gaszähler: ${booleanLabel(
              inspectionData.is_last_gas_meter
            )}`
          : null,
        inspectionData.message_type
          ? `Meldungsart: ${inspectionData.message_type}`
          : null,
      ]);
    },
    joinContentLines(lines) {
      const renderedLines = lines
        .map((line) => formatOptionalValue(line))
        .filter(Boolean)
        .map((line) => escapeHtml(line));

      if (renderedLines.length === 0) {
        return escapeHtml(this.$t('commissioning.details.not_available_value'));
      }

      return renderedLines.join('<br>');
    },
    joinStepLines(fields) {
      const lines = fields
        .filter(
          ([, value]) => value !== null && value !== undefined && value !== ''
        )
        .map(([labelKey, value]) => {
          return `<strong>${escapeHtml(
            this.$t(`commissioning.details.fields.${labelKey}`)
          )}:</strong> ${escapeHtml(value)}`;
        });

      if (lines.length === 0) {
        return escapeHtml(this.$t('commissioning.details.not_available_value'));
      }

      return lines.join('<br>');
    },
  },
  apollo: {
    $subscribe: {
      tags: {
        query: gql`
          subscription onCommissioningHistoryChanged($orderId: String) {
            onCommissioningHistoryChanged(id: $orderId) {
              id
              changed_by
              event_origin
              created
              field
              title
              old_value
              new_value
              workflow_payload
              note
              status
              filename
            }
          }
        `,
        variables() {
          return {
            orderId: this.$route.params.id,
          };
        },
        error(error) {
          console.error('[GraphQL] Commissioning - Subscription error:', error);
          console.error(
            '[GraphQL] Commissioning - Subscription error details:',
            JSON.stringify(error, null, 2)
          );
        },
        result({ data }) {
          const eventData = data.onCommissioningHistoryChanged;
          let oldValueParsed = null;
          let newValueParsed = null;

          try {
            oldValueParsed = this.getChangedValue('old_value', eventData);
            newValueParsed = this.getChangedValue('new_value', eventData);
          } catch (e) {
            console.error('Error parsing history values for Commissioning:', e);
            return;
          }

          const payload = {
            id: eventData.id,
            new_value: newValueParsed,
            old_value: oldValueParsed,
            title:
              eventData.title === '$context.prev.result.payload.title'
                ? null
                : eventData.title,
            note:
              eventData.note === '$context.prev.result.payload.note'
                ? null
                : eventData.note,
            filename: eventData.filename,
            status: this.parseHistoryStatus(eventData.status),
          };

          eventData.payload = JSON.stringify(payload);

          if (newValueParsed) {
            // updateCommissioningOrderRequest/-Data ship order.payload fields (unit, meter_position, ...),
            // merging them at the top level of `order` silently no-ops since the UI reads order.payload.*
            const isOrderPayloadUpdate = [
              'updateCommissioningOrderRequest',
              'updateCommissioningOrderData',
              'updateCommissioningTechnicalInspectionData',
            ].includes(eventData.field);

            if (isOrderPayloadUpdate) {
              this.$store.commit(
                'updateSelectedCommissioningPayloadData',
                newValueParsed
              );
              this.order = {
                ...this.order,
                payload: {
                  ...this.order?.payload,
                  ...newValueParsed,
                },
              };
            } else {
              this.$store.commit(
                'updateSelectedCommissioningData',
                newValueParsed
              );
              this.order = {
                ...this.order,
                ...newValueParsed,
              };
            }
          }

          this.$store.commit(
            'updateLastChangedByInSelectedCommissioning',
            eventData.changed_by
          );
          this.$store.commit(
            'updateLastUpdateInSelectedCommissioning',
            eventData.created
          );
          if (this.order) {
            this.order = {
              ...this.order,
              last_changed_by: eventData.changed_by,
              last_update: eventData.created,
            };
          }

          this.$store.commit('setNotifyEvent', eventData);
          if (payload.filename) {
            this.emitLiveCommissioningDocumentHistoryEvent(eventData);
          } else {
            EventBus.$emit('update-history-table-commissioning', eventData);
          }
        },
      },
    },
  },
};
</script>

<style scoped>
.heading {
  font-size: 50px;
  margin-bottom: 0;
}

.header_container {
  display: flex;
  align-items: center;
}

.header_action_container {
  width: 40%;
  display: flex;
  justify-content: flex-end;
}

.wmsb-review-completion-button.v-btn.v-btn--disabled {
  background-color: #9b004b !important;
  color: white !important;
  opacity: 1 !important;
}

.header_text {
  display: flex;
  flex-direction: column;
  width: 100%;
}

.header_customer {
  font-size: 25px;
}

.header_address {
  font-size: 20px;
}

.back-button {
  border-radius: 40px;
  background-color: rgba(255, 255, 255, 0.9);
  height: 35px !important;
  width: 90px;
  box-shadow: none;
  margin-right: 30px;
}

.info-copy {
  font-size: 18px;
  color: #6e6e6e;
}

.commissioning-details-layout {
  display: flex;
  align-items: flex-start;
}

.commissioning-details-main {
  flex: 1 1 50%;
  min-width: 0;
}

.commissioning-details-side {
  flex: 1 1 50%;
  min-width: 0;
}

.card-without-title > .frosted_glass_card_title {
  margin-bottom: 0 !important;
}

.dialog-title {
  font-size: 24px;
  font-weight: 600;
}

.cancellation-dialog-footer {
  width: 100%;
}

.cancellation-dialog-actions {
  display: flex;
  gap: 8px;
}

.error-message {
  color: #b00020;
  margin-bottom: 12px;
}
</style>
